import {
  WSButton,
  WSElement,
  WSElementProps,
  WSFlexBox,
  WSList,
  WSPill,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  IInvoice,
  IInvoicePayoutDestination,
  InvoicePayoutDestinationType,
  InvoiceStatus
} from "@wingspanhq/payments/dist/interfaces";
import { PreferredPayoutSpeed } from "@wingspanhq/wingspan-api-sdk";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import React from "react";
import { useHistory } from "react-router-dom";
import { PAYOUTS_PATH } from "../../../modules/Payouts";
import { AlertEstimatedTaxWithholdings } from "../../../modules/TaxWithholdings/components/AlertEstimatedTaxWithholdings";
import { AlertTaxWithholdingsEnabled } from "../../../modules/TaxWithholdings/components/AlertTaxWithholdingsEnabled";
import { WSQueries } from "../../../query/WSQuery";
import { queriesInternalAccounts, queriesTaxWithholdings } from "@query";
import { useQueryOwnPayoutSettings } from "@hooks/useQueryOwnPayoutSettings";
import { useQueryInvoice } from "../../../query/payments/queries";
import { getInvoicePayoutDestinationDescription } from "../../../query/payments/selectors";
import { useQueryPayout } from "../../../query/payouts/queries/useQueryPayout";
import { selectorActiveTaxWithholdingAccount } from "../../../shared/selectors/selectorActiveTaxWithholdingAccount";
import { selectorPayoutSettingsInstantFee } from "@selectors/selectorPayoutSettingsInstantFee";
import { instantPayoutService } from "../../../shared/services/instantPayout";
import { openInNewTab } from "../../../utils/openInNewTab";
import { calculateLineItemsTotal } from "../../utils";
import { getLineItemsTotals } from "../../utils/getLineItemsTotals";
import { stackPayoutAmounts } from "../../utils/invoicePayouts";
import { getPayoutMethodName, usePayoutMethods } from "../../utils/payout";
import { Totals, TotalsSection, TotalsSectionItem } from "../Totals/Totals";
import { getInvoiceTaxWithholdings } from "./getInvoiceTaxWithholdings";

type Props = {
  invoiceId: string;
  is1099Flow?: boolean;
} & WSElementProps;

export const InvoiceTotals: React.FC<Props> = ({
  invoiceId,
  is1099Flow,
  ...elementProps
}) => {
  const history = useHistory();
  const queryInvoice = useQueryInvoice(invoiceId);
  const queryPayoutSettings = useQueryOwnPayoutSettings();
  const querySettings =
    queriesTaxWithholdings.getTaxWithholdingSettings.useQuery();
  const queryInternalAccounts =
    queriesInternalAccounts.listInternalAccounts.useQuery();

  const hasActiveTaxWithholdingAccount =
    queryInternalAccounts.data?.data?.some(
      selectorActiveTaxWithholdingAccount
    ) ?? false;

  const { payoutPreferences, payoutMethods } = usePayoutMethods();

  return (
    <WSElement {...elementProps}>
      <WSQueries
        queries={{
          queryInvoice,
          queryPayoutSettings
        }}
      >
        {({
          queryInvoice: { data: invoice },
          queryPayoutSettings: { data: payoutSettings }
        }) => {
          const instantPayoutDefaultFee =
            selectorPayoutSettingsInstantFee(payoutSettings);

          const totalElement =
            invoice.status !== InvoiceStatus.Paid &&
            invoice.status !== InvoiceStatus.PaymentInTransit ? (
              <WSFlexBox.CenterY>
                <WSText mr="M">Total</WSText>
                <WSPill icon="time" text="Pending" />
              </WSFlexBox.CenterY>
            ) : (
              "Total"
            );

          const totalsSections: TotalsSection[] = [];

          // Line items

          const lateFee = invoice.chargedFees?.lateFee?.amount || 0;
          const lineItemsAmount = calculateLineItemsTotal(invoice.lineItems);
          const lineItemsSum = lineItemsAmount + lateFee;

          const totalsLineItemsSection = getLineItemsTotals(
            invoice.lineItems,
            invoice.currency,
            {
              is1099Flow
            }
          );

          if (lateFee > 0) {
            totalsLineItemsSection.items.push([
              "Late fee",
              formatMoney(lateFee, invoice.currency)
            ]);
          }

          totalsSections.push(totalsLineItemsSection);

          // Fees & collaborator payments

          const creditCardFee =
            invoice.creditFeeHandling?.memberPays === 100
              ? (invoice.processingFees?.creditCardFee?.amount || 0) +
                (invoice.amountDetails?.wingspanTopUp || 0)
              : 0;

          const instantPayoutFee = invoice.processingFees?.instantPayoutFee
            ?.amount
            ? invoice.processingFees.instantPayoutFee.amount
            : invoice.status !== InvoiceStatus.Paid &&
                payoutPreferences === PreferredPayoutSpeed.INSTANT
              ? instantPayoutService.calculateInvoiceInstantPayoutFee(
                  lineItemsSum,
                  payoutSettings
                )
              : 0;

          const collaboratorPayments = invoice.amountDetails
            ?.collaboratorPayments
            ? invoice.amountDetails.collaboratorPayments
            : invoice.collaborators
              ? invoice.collaborators.reduce(
                  (total, collaborator) => total + collaborator.amount,
                  0
                )
              : 0;

          const grossIncome =
            lineItemsSum -
            creditCardFee -
            instantPayoutFee -
            collaboratorPayments;

          if (
            creditCardFee > 0 ||
            instantPayoutFee > 0 ||
            collaboratorPayments > 0
          ) {
            const totalsFeesSection: TotalsSection = {
              items: [
                {
                  bold: true,
                  left: totalElement,
                  right: formatMoney(lineItemsSum, invoice.currency)
                }
              ]
            };

            if (creditCardFee) {
              totalsFeesSection.items.push([
                "Credit card fee",
                formatMoney(-creditCardFee, invoice.currency)
              ]);
            }

            if (instantPayoutFee) {
              if (invoice.processingFees?.instantPayoutFee?.amount) {
                totalsFeesSection.items.push([
                  `Instant payout fee (${
                    (invoice.processingFees.instantPayoutFee.percentage || 0) *
                    100
                  }%)`,
                  formatMoney(
                    -invoice.processingFees.instantPayoutFee.amount,
                    invoice.currency
                  )
                ]);
              } else {
                totalsFeesSection.items.push([
                  `Instant payout fee (${instantPayoutDefaultFee}%)`,
                  formatMoney(-instantPayoutFee, invoice.currency)
                ]);
              }
            }

            if (collaboratorPayments) {
              totalsFeesSection.items.push([
                "Payment to contractors",
                formatMoney(-collaboratorPayments, invoice.currency)
              ]);
            }

            totalsSections.push(totalsFeesSection);
          }

          let netIncome = grossIncome;

          // Tax withholdings & deductions
          const { amount: taxWithholdingAmount, rate: taxWithholdingRate } =
            getInvoiceTaxWithholdings(
              querySettings.data,
              invoice,
              netIncome,
              hasActiveTaxWithholdingAccount
            );
          netIncome -= taxWithholdingAmount;

          const deductions = invoice.deductions || [];
          deductions.forEach(deduction => {
            netIncome -= deduction.amount;
          });

          if (deductions.length > 0 || taxWithholdingAmount > 0) {
            const section: TotalsSection = {
              items: [
                {
                  bold: true,
                  left:
                    grossIncome === lineItemsSum
                      ? totalElement
                      : "Gross income",
                  right: formatMoney(grossIncome, invoice.currency)
                },
                ...deductions.map(d => ({
                  left: d.name,
                  right: formatMoney(-d.amount, invoice.currency)
                }))
              ]
            };

            if (taxWithholdingAmount > 0) {
              section.items.push({
                left: `Tax withholdings (${taxWithholdingRate}%)`,
                right: formatMoney(-taxWithholdingAmount, invoice.currency)
              });
            }

            totalsSections.push(section);
          }

          if (invoice.events.markedPaidAt) {
            // Recorded total

            totalsSections.push({
              items: [
                {
                  bold: true,
                  left: "Recorded total",
                  right: formatMoney(netIncome, invoice.currency)
                }
              ]
            });
          } else {
            // Payout destinations

            const payoutDestinationsSection: TotalsSection = {
              items: [
                {
                  bold: true,
                  left: netIncome === grossIncome ? totalElement : "Net income",
                  right: formatMoney(netIncome, invoice.currency)
                }
              ]
            };

            if (
              invoice.payoutDestinations &&
              invoice.payoutDestinations.length > 0
            ) {
              const stacked = stackPayoutAmounts(invoice.payoutDestinations);

              stacked.forEach(payoutDestination => {
                const item = payoutDestinationToTotals(
                  payoutDestination,
                  invoice,
                  history
                );

                if (item) {
                  payoutDestinationsSection.items.push(item);
                }
              });
            } else {
              if (payoutPreferences === PreferredPayoutSpeed.INSTANT) {
                payoutMethods.instant.active.forEach(payoutMethod => {
                  payoutDestinationsSection.items.push([
                    getPayoutMethodName(payoutMethod),
                    formatMoney(
                      (netIncome * (payoutMethod.percentage || 0)) / 100,
                      invoice.currency
                    )
                  ]);
                });
              } else {
                payoutMethods.standard.active.forEach(payoutMethod => {
                  payoutDestinationsSection.items.push([
                    getPayoutMethodName(payoutMethod),
                    formatMoney(
                      (netIncome * (payoutMethod.percentage || 0)) / 100,
                      invoice.currency
                    )
                  ]);
                });
              }
            }

            totalsSections.push(payoutDestinationsSection);
          }

          return (
            <WSList gap="XL">
              <Totals sections={totalsSections} />

              {!invoice.events.markedPaidAt && (
                <>
                  <AlertEstimatedTaxWithholdings
                    invoiceNetAmount={netIncome}
                    invoiceCurrency={invoice.currency}
                  />
                  <AlertTaxWithholdingsEnabled />
                </>
              )}
            </WSList>
          );
        }}
      </WSQueries>
    </WSElement>
  );
};

function payoutDestinationToTotals(
  payoutDestination: IInvoicePayoutDestination,
  invoice: IInvoice,
  history: ReturnType<typeof useHistory>
): TotalsSectionItem | undefined {
  if (
    payoutDestination.destinationType === InvoicePayoutDestinationType.Payout
  ) {
    return [
      <WSFlexBox key="left" alignItems="center" gap="M">
        <WSText color="gray500">Payout</WSText>

        <WSButton.Link
          size="S"
          rightIcon="open-tab"
          onClick={() => {
            history.push(PAYOUTS_PATH + "/" + payoutDestination.destinationId);
          }}
        >
          View
        </WSButton.Link>
      </WSFlexBox>,
      <PayoutAmount
        key="right"
        amount={payoutDestination.amount}
        payoutId={payoutDestination.destinationId!}
      />
    ];
  }

  if (
    payoutDestination.destinationType === InvoicePayoutDestinationType.WeGift
  ) {
    return [
      <WSButton.Link
        key="left"
        rightIcon="arrow-right"
        onClick={() => {
          if (payoutDestination.destinationId)
            openInNewTab(payoutDestination.destinationId);
        }}
      >
        Redeem gift card
      </WSButton.Link>,
      null
    ];
  }

  return [
    <WSFlexBox key="left" alignItems="center" gap="M">
      <WSText color="gray500">
        {getInvoicePayoutDestinationDescription(payoutDestination)}
      </WSText>
    </WSFlexBox>,
    formatMoney(payoutDestination.amount, invoice.currency)
  ];
}

const PayoutAmount: React.FC<{ amount: number; payoutId: string }> = props => {
  const queryPayout = useQueryPayout(props.payoutId);

  if (!queryPayout.data) {
    return null;
  }

  return (
    <WSText color="gray500">
      {formatMoney(props.amount, queryPayout.data.currency ?? "USD")}
    </WSText>
  );
};
